import { authState, invitationStateV2 } from '../stores/shared';
import { getStateStore } from '../stores';
import constants from '@playcastdotio/GlobalConfig';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { toast } from 'sonner';
import { addObservabilityHeaders } from '@playcastdotio/Observability';
import { getHostCorrelationIds } from './realtime/hostCorrelation';

export const ensureTrailingSlash = (url: string) => {
  if (url.endsWith('/')) return url;
  return url.concat('/');
};

export const send = async (method: string, url: string, data?: Record<string, any>, urlOverride?: string) => {
  let accessToken = authState.currentState.accessToken;
  if (!accessToken) {
    accessToken = await new Promise<string>((resolve, reject) => {
      const listener = authState.addListener((state) => {
        if (state.accessToken) {
          authState.removeListener(listener);
          resolve(state.accessToken);
        }
      });
      setTimeout(() => {
        reject(new Error('Timeout waiting for access token'));
      }, 10000);
    });
  }

  const headers: Record<string, string> = {
    Authorization: 'Bearer '.concat(accessToken),
    'Content-Type': 'application/json',
  };

  // Stamp hierarchical correlation headers so realtime-api tags the request.
  const ids = getHostCorrelationIds();
  addObservabilityHeaders(new Headers(), () => ids.machineId, () => ids.broadcastId).forEach(
    (value, key) => {
      headers[key] = value;
    },
  );

  const config: AxiosRequestConfig = {
    method,
    maxBodyLength: Infinity,
    url: ensureTrailingSlash(urlOverride ?? constants.REALTIME_API_BASE_URL).concat(url),
    headers,
  };

  if (['post', 'put', 'patch'].includes(method) && data) {
    config.data = JSON.stringify(data);
  }

  try {
    const response = await axios.request(config);
    return response.data;
  } catch (error: unknown) {
    const e = error as AxiosError<{ error: string }>;
    console.error('[API] Failed to post to API: ', e.code, config);
    return {
      success: false,
      error: e.response?.data?.error,
    };
  }
};

const isLocal = constants.VERSION.endsWith('-local') && constants.REALTIME_API_BASE_URL.includes('localhost');

export const twitchJoinChannel = async () => {
  const state = getStateStore('ipc');
  if (state.twitchSettingsState.currentState.twitchDisplayName === '') {
    console.error('[API] Cannot join channel without a display name');
    return;
  }
  if (!state.twitchSettingsState.currentState.isTwitchBotEnabled) {
    console.error('[API] Cannot join channel without Twitch bot enabled');
    return;
  }
  send('post', 'twitch/bot/join', undefined, isLocal ? 'http://localhost:3200' : undefined);
};
export const twitchLeaveChannel = async () => {
  send('post', 'twitch/bot/leave', undefined, isLocal ? 'http://localhost:3200' : undefined);
};
let startListener: string | undefined;
export const twitchSendStartAnnouncement = async () => {
  const state = getStateStore('ipc');
  if (!state.twitchSettingsState.currentState.isSendStartAnnouncement) {
    console.error('[API] Cannot send start announcement without it enabled');
    return;
  }
  if (!state.invitationStateV2.currentState?.id) {
    startListener = state.invitationStateV2.addListener((invState) => {
      if (invState.id) {
        if (startListener) state.invitationStateV2.removeListener(startListener);
        const type = state.invitationStateV2.currentState.type;
        const streamUrl = `${type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${state.invitationStateV2.currentState.id}`;
        send('post', 'twitch/events/started', { streamUrl }, isLocal ? 'http://localhost:3200' : undefined);
        startListener = undefined;
      }
    });
    setTimeout(() => {
      if (state.invitationStateV2.currentState?.id) {
        if (startListener) state.invitationStateV2.removeListener(startListener);
        const type = state.invitationStateV2.currentState.type;
        const streamUrl = `${type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${state.invitationStateV2.currentState.id}`;
        send('post', 'twitch/events/started', { streamUrl }, isLocal ? 'http://localhost:3200' : undefined);
        startListener = undefined;
      }
    }, 1000);
  } else {
    const type = state.invitationStateV2.currentState.type;
    const streamUrl = `${type === 'cafe' ? constants.PAVILION_PLAYER_BASE_URL : constants.PRIME_PLAYER_BASE_URL}j/${state.invitationStateV2.currentState.id}`;
    send('post', 'twitch/events/started', { streamUrl }, isLocal ? 'http://localhost:3200' : undefined);
  }
};
export const twitchSendEndAnnouncement = async () => {
  const state = getStateStore('ipc');
  if (startListener) {
    state.invitationStateV2.removeListener(startListener);
    startListener = undefined;
  }
  send(
    'post',
    'twitch/events/stopped',
    {
      shouldSendMessage: state.twitchSettingsState.currentState.isSendEndAnnouncement,
    },
    isLocal ? 'http://localhost:3200' : undefined,
  );
};
export const twitchSendReminder = async (streamUrl: string) => {
  if (!streamUrl) {
    console.error('[API] Cannot send reminder without a streamUrl');
    return;
  }
  console.warn('[TWITCH] sending reminder');
  const state = getStateStore('ipc');
  if (!state.twitchSettingsState.currentState.isSendStreamUrlReminder) {
    console.error('[API] Cannot send reminder without it enabled');
    return;
  }
  send('post', 'twitch/events/reminder', { streamUrl }, isLocal ? 'http://localhost:3200' : undefined);
};
export const twitchSendNotify = async () => {
  const state = getStateStore('ipc');
  console.warn('[TWITCH] sending notify');
  if (!state.twitchSettingsState.currentState.isSendPlaycastInfo) {
    console.error('[API] Cannot send playcast info without it enabled');
    return;
  }
  send('post', 'twitch/events/notify', undefined, isLocal ? 'http://localhost:3200' : undefined);
};
export const discordSendStartAnnouncement = async () => {
  const state = getStateStore('ipc');
  if (!state.discordSettingsState.currentState.isDiscordBotEnabled) {
    console.error('[API] Cannot send start announcement without Discord bot enabled');
    return;
  }
  const streamTitle = state.invitationStateV2.currentState.description || state.invitationStateV2.currentState.name;
  send('post', 'discord/events/stream-start', { streamTitle }, isLocal ? 'http://localhost:3100' : undefined);
};
export const discordSendEndAnnouncement = async () => {
  const state = getStateStore('ipc');
  if (!state.discordSettingsState.currentState.isDiscordBotEnabled) {
    console.error('[API] Cannot send end announcement without Discord bot enabled');
    return;
  }
  send('post', 'discord/events/stream-stop', undefined, isLocal ? 'http://localhost:3100' : undefined);
};

let currentTwitchPromise: Promise<any> | null = null;
export const sendTwitchBot = async (invitationId: string) => {
  if (currentTwitchPromise) {
    return currentTwitchPromise;
  }
  currentTwitchPromise = new Promise((resolve, reject) => {
    const toastId = toast.loading('Sending lobby announcement...', {
      style: {
        color: '#ffffffaa',
      },
      duration: 3000,
    });
    send(
      'post',
      'twitch/events/lobby',
      {
        invitationId,
      },
      isLocal ? 'http://localhost:3200' : undefined,
    )
      .then((res) => {
        // Show toast with result and optional error message
        if (res?.success) {
          toast.success('Lobby announcement sent successfully', {
            id: toastId,
            style: {
              // Green color
              color: '#00aa00',
            },
          });
        } else {
          toast.error(res?.error || 'Failed to send lobby announcement', {
            id: toastId,
            style: {
              // Red color
              color: '#aa0000',
            },
          });
        }
        console.log(res);
        resolve(res);
        currentTwitchPromise = null;
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || 'Failed to send lobby announcement', {
          id: toastId,
          style: {
            color: '#aa0000',
          },
        });
        console.error(error);
        reject(error);
        currentTwitchPromise = null;
      });
  });
  return currentTwitchPromise;
};

let currentDiscordPromise: Promise<any> | null = null;
export const sendDiscordBot = async (invitationId: string) => {
  if (currentDiscordPromise) {
    return currentDiscordPromise;
  }
  currentDiscordPromise = new Promise((resolve, reject) => {
    const toastId = toast.loading('Sending lobby announcement...', {
      style: {
        color: '#ffffffaa',
      },
      duration: 3000,
    });
    const lobbyTitle = invitationStateV2.currentState.name;
    send(
      'post',
      'discord/events/lobby',
      {
        invitationId,
        title: lobbyTitle,
      },
      isLocal ? 'http://localhost:3100' : undefined,
    )
      .then((res) => {
        // Show toast with result and optional error message
        if (res?.success) {
          toast.success('Lobby announcement sent successfully', {
            id: toastId,
            style: {
              // Green color
              color: '#00aa00',
            },
          });
        } else {
          toast.error(res?.error || 'Failed to send lobby announcement', {
            id: toastId,
            style: {
              // Red color
              color: '#aa0000',
            },
          });
        }
        console.log(res);
        resolve(res);
        currentDiscordPromise = null;
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || 'Failed to send lobby announcement', {
          id: toastId,
          style: {
            color: '#aa0000',
          },
        });
        console.error(error);
        reject(error);
        currentDiscordPromise = null;
      });
  });
  return currentDiscordPromise;
};
